import { selectClient, selectLtcFinancialPlanning } from '@client/model/selectors';
import { AppState } from '@client/model/store';
import {
  calculateTotalExistingAssetsValue,
  calculateTotalPolicyPremiumCost,
} from '@client/util/fundingSufficiency';
import { isPolicyAddedViaShopInsurance } from '@client/util/policyFundingSourceUtils';
import { useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';

// Navigation type - exported here to avoid circular imports
export interface LtcFinancialPlanningNavigation {
  currentStep: LtcFinancialPlanningStep;
  currentSubStep: string | null;
  goToStep: (step: LtcFinancialPlanningStep, substep?: string) => void;
  goNext: () => void;
  goPrev: () => void;
  progressBarStep: number;
  canGoPrev: boolean;
  canGoNext: boolean;
  continueButtonText: string;
}

export const LTC_FINANCIAL_PLANNING_STEPS: LtcFinancialPlanningStep[] = [
  'asset-selection',
  'asset-details',
  'coverage-assessment',
  'policies',
  'comparison',
];

export const STEP_TO_PROGRESS_BAR: Record<LtcFinancialPlanningStep, number> = {
  'asset-selection': 1,
  'asset-details': 1,
  'coverage-assessment': 1,
  'policies': 2,
  'comparison': 3,
};

export function getStepIndex(step: LtcFinancialPlanningStep): number {
  return LTC_FINANCIAL_PLANNING_STEPS.indexOf(step);
}

export function getNextStep(
  step: LtcFinancialPlanningStep,
  fullState?: AppState,
): LtcFinancialPlanningStep | null {
  const index = getStepIndex(step);
  if (index >= LTC_FINANCIAL_PLANNING_STEPS.length - 1) return null;

  return LTC_FINANCIAL_PLANNING_STEPS[index + 1];
}

export function getPrevStep(
  step: LtcFinancialPlanningStep,
  fullState?: AppState,
): LtcFinancialPlanningStep | null {
  const index = getStepIndex(step);
  if (index <= 0) return null;

  return LTC_FINANCIAL_PLANNING_STEPS[index - 1];
}

export function getProgressBarStep(step: LtcFinancialPlanningStep): number {
  return STEP_TO_PROGRESS_BAR[step] ?? 1;
}

// Custom button text function for each step - can be customized based on state
export type ContinueButtonTextFunction = (
  step: LtcFinancialPlanningStep,
  state: AppState['ltcFinancialPlanning'],
  fullState?: AppState,
) => string;

export const STEP_CONTINUE_BUTTON_TEXT: Record<
  LtcFinancialPlanningStep,
  string | ContinueButtonTextFunction
> = {
  'asset-selection': 'Continue to Account Details',
  'asset-details': 'Calculate My Coverage',
  'coverage-assessment': 'Continue',
  'policies': (step, state, fullState) => {
    const ltcPolicies = fullState?.client?.data?.multipleFundingSources?.ltcPolicy || {};
    const policiesAddedViaShop = Object.values(ltcPolicies).filter(policy =>
      isPolicyAddedViaShopInsurance(policy),
    );
    return policiesAddedViaShop.length > 0 ? 'Continue' : 'Continue without Protection';
  },
  'comparison': 'Continue',
};

export function getContinueButtonText(
  step: LtcFinancialPlanningStep,
  state: AppState['ltcFinancialPlanning'],
  fullState?: AppState,
): string {
  const buttonTextConfig = STEP_CONTINUE_BUTTON_TEXT[step];

  if (typeof buttonTextConfig === 'function') {
    return buttonTextConfig(step, state, fullState);
  }

  return buttonTextConfig ?? 'Continue';
}

export const PROGRESS_STEPS = [
  { id: 1, name: 'Your Assets' },
  { id: 2, name: 'Options' },
  { id: 3, name: 'Your Plan' },
];

// Validation messages for each step
export const STEP_VALIDATION: Record<
  LtcFinancialPlanningStep,
  {
    validate: (state: AppState['ltcFinancialPlanning'], fullState?: AppState) => boolean;
    errorMessage: string;
  }
> = {
  'asset-selection': {
    validate: state => {
      return state.selectedAccountTypes.length > 0;
    },
    errorMessage: 'Please select at least one account to continue',
  },
  'asset-details': {
    validate: (state, fullState) => {
      const selectedAccountTypes = state.selectedAccountTypes;
      const selfFunding = fullState?.client?.data?.multipleFundingSources?.selfFunding || {};
      const selfFundingSources = Object.values(selfFunding);

      // Check that for each selected account type, there's a completed funding source
      return selectedAccountTypes.every(accountType => {
        const matchingSource = selfFundingSources.find(
          (source: any) =>
            source.accountType === accountType || source.selfFundingName === accountType,
        );

        if (!matchingSource) return false;

        // Check if the source is complete
        return (
          matchingSource.existingAssetsValue !== null &&
          matchingSource.annualRateOfReturn !== null &&
          matchingSource.gainsTaxRate !== null
        );
      });
    },
    errorMessage: 'Please complete all account details to continue',
  },
  'coverage-assessment': {
    validate: () => true,
    errorMessage: '',
  },
  'policies': {
    validate: (state: AppState['ltcFinancialPlanning'], fullState?: AppState) => {
      if (!fullState) return true;

      const multipleFundingSources = fullState?.client?.data?.multipleFundingSources;
      if (!multipleFundingSources) return true;

      // Get policies added via shop insurance
      const ltcPolicies = multipleFundingSources.ltcPolicy || {};
      const policiesAddedViaShop = Object.values(ltcPolicies).filter((policy: any) =>
        isPolicyAddedViaShopInsurance(policy),
      );

      // If no policies selected, allow continuation
      if (policiesAddedViaShop.length === 0) return true;

      // Calculate total policy cost and existing assets
      const totalPolicyCost = calculateTotalPolicyPremiumCost(multipleFundingSources);
      const totalExistingAssetsValue = calculateTotalExistingAssetsValue(multipleFundingSources);

      // Return true if funded, false if deficit
      return totalExistingAssetsValue >= totalPolicyCost;
    },
    errorMessage:
      'Your selected policies cost more than your available funding. Please adjust your funding or remove policies to continue.',
  },
  'comparison': {
    validate: () => true, // Final step - navigates back to old financing flow
    errorMessage: '',
  },
};

export function getPhaseProgress(phaseId: number, currentStep: LtcFinancialPlanningStep): number {
  const currentPhase = STEP_TO_PROGRESS_BAR[currentStep];

  // Past this phase = 100%
  if (currentPhase > phaseId) return 100;

  // Haven't reached this phase = 0%
  if (currentPhase < phaseId) return 0;

  // Currently in this phase - calculate based on completed steps
  const stepsInPhase = LTC_FINANCIAL_PLANNING_STEPS.filter(
    s => STEP_TO_PROGRESS_BAR[s] === phaseId,
  );
  const currentStepIndex = getStepIndex(currentStep);
  const phaseStartIndex = getStepIndex(stepsInPhase[0]);

  // Steps completed = current position minus phase start (0 on first step)
  const completedInPhase = currentStepIndex - phaseStartIndex;

  return (completedInPhase / stepsInPhase.length) * 100;
}

export function useLtcFinancialPlanningNavigation() {
  const [searchParams, setSearchParams] = useSearchParams();
  const client = useSelector(selectClient);
  const ltcFinancialPlanningState = useSelector<AppState, AppState['ltcFinancialPlanning']>(
    selectLtcFinancialPlanning,
  );

  // Check if self-funding sources exist
  const selfFunding = client.multipleFundingSources?.selfFunding || {};
  const hasSelfFundingSources = Object.keys(selfFunding).length > 0;

  const currentStep = (searchParams.get('step') as LtcFinancialPlanningStep) || 'asset-selection';
  const currentSubStep = searchParams.get('substep'); // For pages with multiple questions

  const goToStep = useCallback(
    (step: LtcFinancialPlanningStep, substep?: string) => {
      const params = new URLSearchParams(searchParams);
      params.set('step', step);
      if (substep) {
        params.set('substep', substep);
      } else {
        params.delete('substep');
      }
      setSearchParams(params);
    },
    [searchParams, setSearchParams],
  );

  const goNext = useCallback(() => {
    const nextStep = getNextStep(currentStep);
    if (nextStep) {
      goToStep(nextStep);
    }
  }, [currentStep, goToStep]);

  const goPrev = useCallback(() => {
    const prevStep = getPrevStep(currentStep);
    if (prevStep) {
      goToStep(prevStep);
    }
  }, [currentStep, goToStep]);

  // Determine if user can go back
  // If on asset-details and self-funding sources exist, prevent going back to asset-selection
  const canGoPrev =
    currentStep === 'asset-details' && hasSelfFundingSources
      ? false
      : getPrevStep(currentStep) !== null;

  return {
    currentStep,
    currentSubStep,
    goToStep,
    goNext,
    goPrev,
    progressBarStep: getProgressBarStep(currentStep),
    canGoPrev,
    canGoNext: getNextStep(currentStep) !== null,
    continueButtonText: getContinueButtonText(currentStep, ltcFinancialPlanningState, {
      client: { data: client },
    } as AppState),
  };
}
